// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna_types/PlotQuadraticSpecialPointsTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview, parseFractionLikeInput } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

/* =============================================================
   TASK: PlotQuadraticSpecialPointsTask
   Punktacja (3 pkt):
   - 1 pkt: miejsca zerowe (x1 i x2 razem; kolejność dowolna)
   - 1 pkt: wierzchołek W(p,q) (p i q razem)
   - 1 pkt: punkt przecięcia z osią OY (y0 = c)
   Zmiany:
   - wierzchołek i oba miejsca zerowe w kadrze ([-6,6]×[-6,6])
   - Δ>0 z całkowitym sqrtΔ
   - check/render odporne na formaty i kolejność argumentów
   - c (y0) jako UŁAMEK MIESZANY w treści/odpowiedzi/wyjaśnieniach
   - WYKRES: etykiety w zapisie dziesiętnym z przecinkiem; punkty pomarańczowe; wykres ciemnozielony
   - unikanie nakładania etykiet: |p| ≥ 2.0 (siatka co 0.5)
   - NOWE: wierzchołek literą „W” (w treści, odpowiedzi, wyjaśnieniach i na wykresie)
   - NOWE: |c| ≤ 7 (punkt OY głębiej w kadrze)
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do { v = Math.floor(Math.random() * span) + min; } while (exclude.has(v));
  return v;
}

const par = (v) => (v < 0 ? `(${v})` : `${v}`);

function gcd(a, b){ a=Math.abs(a); b=Math.abs(b); while(b){const t=a%b;a=b;b=t;} return a||1; }
function simplifyFrac(n, d){
  const s = Math.sign(n)*Math.sign(d) || 1;
  const an = Math.abs(n), ad = Math.abs(d);
  const g = gcd(an, ad);
  return { n: s*(an/g), d: ad/g };
}
function formatFractionMixed(n, d){
  const sgn = Math.sign(n) < 0 ? "-" : "";
  const N = Math.abs(n), D = Math.abs(d);
  if (N % D === 0) return `${sgn}${N/D}`;
  const whole = Math.floor(N/D);
  const rem = N % D;
  if (whole === 0) return `${sgn}\\dfrac{${N}}{${D}}`;
  return `${sgn}${whole}\\dfrac{${rem}}{${D}}`;
}

// plain → decimal with comma (for graph labels)
function fractionToDecimalComma(n, d, maxPlaces = 2) {
  const val = n / d;
  let s = val.toFixed(maxPlaces).replace(/\.?0+$/, "");
  return s.replace(".", ",");
}
// build "(x; y)" label (semicolon to avoid ambiguity with decimal comma)
function labelDecimalXY(xFrac, yFrac, prefix=null) {
  const sx = fractionToDecimalComma(xFrac.n, xFrac.d);
  const sy = fractionToDecimalComma(yFrac.n, yFrac.d);
  return prefix ? `${prefix} (${sx}; ${sy})` : `(${sx}; ${sy})`;
}

// Klasyczny zapis ax^2 + bx + c (zwykłe liczby)
function formatQuadraticLatex(a, b, c) {
  const term = (coeff, base, omitOne=false) => {
    if (coeff === 0) return "";
    const sign = coeff > 0 ? " + " : " - ";
    const abs = Math.abs(coeff);
    const num = (abs === 1 && omitOne) ? "" : String(abs);
    return `${sign}${num}${base}`;
  };
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let out = `${aCoeff}x^2${term(b,"x",true)}`;
  if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
  return out;
}

// ax^2 + bx + c, gdzie c podane jako ułamek mieszany
function formatQuadraticLatexWithMixedC(a, b, cFrac) {
  const term = (coeff, base, omitOne=false) => {
    if (coeff === 0) return "";
    const sign = coeff > 0 ? " + " : " - ";
    const abs = Math.abs(coeff);
    const num = (abs === 1 && omitOne) ? "" : String(abs);
    return `${sign}${num}${base}`;
  };
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let out = `${aCoeff}x^2${term(b,"x",true)}`;
  if (cFrac?.n) {
    const cAbs = formatFractionMixed(Math.abs(cFrac.n), cFrac.d);
    out += (cFrac.n > 0 ? " + " : " - ") + cAbs;
  }
  return out;
}

// parser odporny na: number | {parsed} | raw string
function parseSmart(rawIn) {
  const raw = rawIn?.raw ?? rawIn ?? "";
  try {
    const r = parseFractionLikeInput ? parseFractionLikeInput(raw) : null;
    if (typeof r === "number") return Number.isFinite(r) ? r : null;
    if (r && typeof r === "object" && r.parsed != null) return Number.isFinite(r.parsed) ? r.parsed : null;
  } catch {}
  let s = String(raw ?? "").trim().replace(/\u2212/g, "-").replace(",", ".");
  if (!s) return null;
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1],10), B = parseInt(m[2],10), C = parseInt(m[3],10);
    if (!C) return null;
    const frac = B / C;
    return A >= 0 ? (A + frac) : (A - frac);
  }
  m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const P = parseInt(m[1],10), Q = parseInt(m[2],10);
    if (!Q) return null;
    return P / Q;
  }
  if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

export function generateTask() {
  // siatka pół-krokowa dla p
  const P_GRID = Array.from({ length: 25 }, (_, i) => -6 + i * 0.5) // [-6, -5.5, …, 6]
    .filter(p => Math.abs(p) >= 2.0);

  // 1) wybór a
  const ABSA = [1, 2, 3][Math.floor(Math.random() * 3)];
  const a = (Math.random() < 0.5 ? -1 : 1) * ABSA;

  // 2) maksymalne k od okna po q:  |q| = |a| k^2 <= 6  =>  k <= floor(sqrt(6/|a|))
  const kMaxByQ = Math.floor(Math.sqrt(6 / Math.abs(a)));
  if (kMaxByQ < 1) {
    // awaryjnie — nie powinno się zdarzać
    return fallbackTask();
  }

  // 3) wybór k (nie za duży, by lista p nie była pusta)
  const k = randInt(1, kMaxByQ);

  // 4) filtr p: korzenie i |c| <= 7
  const allowedP = P_GRID.filter(p => {
    const kMaxByRoots = Math.floor(6 - Math.abs(p));
    if (k > kMaxByRoots) return false; // korzenie wyszłyby poza kadr
    const c = a * (p * p - k * k);
    return Math.abs(c) <= 7;           // punkt OY w kadrze jak chciałeś
  });

  if (allowedP.length === 0) {
    // spróbuj mniejsze k
    for (let kk = k - 1; kk >= 1; kk--) {
      const ok = P_GRID.filter(p => {
        const kMaxByRoots = Math.floor(6 - Math.abs(p));
        if (kk > kMaxByRoots) return false;
        const c = a * (p * p - kk * kk);
        return Math.abs(c) <= 7;
      });
      if (ok.length) {
        return buildTaskFrom(a, ok[Math.floor(Math.random() * ok.length)], kk);
      }
    }
    return fallbackTask();
  }

  // 5) mamy gotowe p — składamy zadanie
  const p = allowedP[Math.floor(Math.random() * allowedP.length)];
  return buildTaskFrom(a, p, k);
}

// --- pomocniki do budowy i fallbacku (używają Twojej dotychczasowej logiki) ---
function buildTaskFrom(a, p, k) {
  const b = -2 * a * p;
  const c = a * (p * p - k * k);
  const q = -a * k * k;
  const x1 = p - k, x2 = p + k;
  const sqrtDelta = 2 * Math.abs(a) * k;
  const delta = sqrtDelta * sqrtDelta;

  const pFrac  = simplifyFrac(-(-2 * a * p), 2 * a);
  const qFrac  = simplifyFrac(-delta, 4 * a);
  const x1Frac = simplifyFrac(-b - sqrtDelta, 2 * a);
  const x2Frac = simplifyFrac(-b + sqrtDelta, 2 * a);

  const pNum = Math.round(p * 2); // p = pNum/2
  const y0Frac = simplifyFrac(a * (pNum * pNum - 4 * k * k), 4);

  return {
    type: "PlotQuadraticSpecialPointsTask",
    data: {
      a, b, c,
      p: pFrac.n / pFrac.d,
      q: qFrac.n / qFrac.d,
      x1: x1Frac.n / x1Frac.d,
      x2: x2Frac.n / x2Frac.d,
      y0: c,
      delta, sqrtDelta,
      pFrac, qFrac, x1Frac, x2Frac,
      y0Frac,
    },
  };
}

function fallbackTask() {
  const a = 1, p = 2, k = 2, q = -a * k * k;
  const b = -2 * a * p, c = a * (p * p - k * k);
  const sqrtDelta = 2 * Math.abs(a) * k, delta = sqrtDelta * sqrtDelta;
  const y0Frac = simplifyFrac(a * (Math.round(p*2)**2 - 4 * k * k), 4);
  return {
    type: "PlotQuadraticSpecialPointsTask",
    data: {
      a, b, c,
      p, q, x1: p - k, x2: p + k, y0: c,
      delta, sqrtDelta,
      pFrac:  simplifyFrac(-b, 2 * a),
      qFrac:  simplifyFrac(-delta, 4 * a),
      x1Frac: simplifyFrac(-b - sqrtDelta, 2 * a),
      x2Frac: simplifyFrac(-b + sqrtDelta, 2 * a),
      y0Frac,
    },
  };
}


/* ---------- render ---------- */

export function renderTask({ data }) {
  // w treści: c jako ułamek mieszany
  const body = formatQuadraticLatexWithMixedC(data.a, data.b, data.y0Frac);
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci ogólnej: <InlineMath math={'f(x) = ' + body} />
      </div>
      <div>
        Oblicz: <strong>Miejsca zerowe:</strong> <InlineMath math={'x_1,\\ x_2'} />,{" "}
        <InlineMath math={'W(p,\\ q)'} /> oraz <strong>punkt przecięcia z osią OY</strong>.
      </div>
    </div>
  );
}

/* ---------- inputs ---------- */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const norm = (v) => (v && typeof v === "object" && "raw" in v) ? v : { raw: String(v ?? "") };

  // wspieramy też stary zapis userAnswer.pq.{x,y} (żeby nie wywalić starych zapisanych odpowiedzi)
  const legacyP = userAnswer?.pq?.x;
  const legacyQ = userAnswer?.pq?.y;

  const val = {
    x1: norm(userAnswer?.x1 ?? ""),
    x2: norm(userAnswer?.x2 ?? ""),
    p:  norm(userAnswer?.p  ?? legacyP ?? ""),
    q:  norm(userAnswer?.q  ?? legacyQ ?? ""),
    y0: norm(userAnswer?.y0 ?? ""),
  };

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'x_1 ='} />
        <FractionInput
          value={val.x1}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), x1: v })}
          disabled={disabled}
          label="x_1"
        />
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'x_2 ='} />
        <FractionInput
          value={val.x2}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), x2: v })}
          disabled={disabled}
          label="x_2"
        />
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'W(p,\\ q) ='} />
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gap: 6 }}>
            <InlineMath math={'p ='} />
            <FractionInput
              value={val.p}
              onChange={(v) => onChange({ ...(userAnswer ?? {}), p: v })}
              disabled={disabled}
              label="p"
            />
          </div>

          <div style={{ display: "grid", gap: 6 }}>
            <InlineMath math={'q ='} />
            <FractionInput
              value={val.q}
              onChange={(v) => onChange({ ...(userAnswer ?? {}), q: v })}
              disabled={disabled}
              label="q"
            />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <span>Punkt przecięcia z osią OY:</span>
        <InlineMath math={'y_0 ='} />
        <FractionInput
          value={val.y0}
          onChange={(v) => onChange({ ...(userAnswer ?? {}), y0: v })}
          disabled={disabled}
          label="y_0"
        />
      </div>
    </div>
  );
}

/* ---------- check ---------- */

export function checkAnswer({ data }, userAnswer) {
  try {
    if (!userAnswer) {
      return { isCorrect: false, score: 0, maxScore: 3, parts: { roots: false, vertex: false, y0: false } };
    }

    const r1 = parseSmart(userAnswer?.x1);
    const r2 = parseSmart(userAnswer?.x2);
    if (r1 == null || r2 == null) {
      return { isCorrect: false, score: 0, maxScore: 3, parts: { roots: false, vertex: false, y0: false } };
    }

    const gRoots = [r1, r2].sort((u,v)=>u-v);
    const cRoots = [data.x1, data.x2].sort((u,v)=>u-v);

    const close = (x,y) => Math.abs(x - y) < 0.01;
    const rootsOk = close(gRoots[0], cRoots[0]) && close(gRoots[1], cRoots[1]);

    // nowy zapis: userAnswer.p i userAnswer.q
    // stary zapis: userAnswer.pq.{x,y}
    const pGiven = parseSmart(userAnswer?.p ?? userAnswer?.pq?.x);
    const qGiven = parseSmart(userAnswer?.q ?? userAnswer?.pq?.y);
    const vertexOk = (pGiven != null && qGiven != null) && close(pGiven, data.p) && close(qGiven, data.q);

    const y0Given = parseSmart(userAnswer?.y0);
    const y0Ok = (y0Given != null) && close(y0Given, data.y0);

    const parts = { roots: rootsOk, vertex: vertexOk, y0: y0Ok };
    const score = (rootsOk ? 1 : 0) + (vertexOk ? 1 : 0) + (y0Ok ? 1 : 0);
    return { isCorrect: score === 3, score, maxScore: 3, parts };
  } catch {
    return { isCorrect: false, score: 0, maxScore: 3, parts: { roots: false, vertex: false, y0: false } };
  }
}

/* ---------- user answer ---------- */

export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data && typeof x.data === "object";
  const task = looksLikeTask(arg1) ? arg1 : (looksLikeTask(arg2) ? arg2 : { data: {} });
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const x1raw = userAnswer?.x1?.raw ?? userAnswer?.x1 ?? "";
  const x2raw = userAnswer?.x2?.raw ?? userAnswer?.x2 ?? "";
  const praw  = userAnswer?.p?.raw ?? userAnswer?.p ?? userAnswer?.pq?.x?.raw ?? userAnswer?.pq?.x ?? "";
  const qraw  = userAnswer?.q?.raw ?? userAnswer?.q ?? userAnswer?.pq?.y?.raw ?? userAnswer?.pq?.y ?? "";
  const y0raw = userAnswer?.y0?.raw ?? userAnswer?.y0 ?? "";

  const empty = (s) => !String(s ?? "").trim();
  const allEmpty = [x1raw, x2raw, praw, qraw, y0raw].every(empty);
  if (allEmpty) return <i>(brak odpowiedzi)</i>;

  const show = (r) => (formatLatexPreview(r) || "?");

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`x_1 = ${show(x1raw)},\\quad x_2 = ${show(x2raw)}`} />
      <div style={{ height: 6 }} />
      <InlineMath math={`W(p,\\ q) = \\big(${show(praw)},\\ ${show(qraw)}\\big)`} />
      <div style={{ height: 6 }} />
      <InlineMath math={`y_0 = ${show(y0raw)}`} />
    </div>
  );
}

/* ---------- official answer ---------- */

export function renderAnswer({ data }) {
  const roots = [
    { n: data.x1Frac.n, d: data.x1Frac.d, v: data.x1 },
    { n: data.x2Frac.n, d: data.x2Frac.d, v: data.x2 },
  ].sort((p,q)=>p.v-q.v);

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`x_1 = ${formatFractionMixed(roots[0].n, roots[0].d)},\\quad x_2 = ${formatFractionMixed(roots[1].n, roots[1].d)}`} />
      <div style={{ height: 6 }} />
      {/* NOWE: wierzchołek z „W = ...” */}
      <InlineMath math={`W = \\Big(${formatFractionMixed(data.pFrac.n, data.pFrac.d)},\\ ${formatFractionMixed(data.qFrac.n, data.qFrac.d)}\\Big)`} />
      <div style={{ height: 6 }} />
      <InlineMath math={`y_0 = ${formatFractionMixed(data.y0Frac.n, data.y0Frac.d)}`} />
    </div>
  );
}

/* ---------- explanation + graph ---------- */

export function renderExplanation({ data }) {
  const { a, b, c, delta, sqrtDelta, pFrac, qFrac, x1Frac, x2Frac, p, q, x1, x2 } = data;
  const twoA = 2 * a;
  const b2 = b * b;
  const fourAC = 4 * a * c;

  // decimal labels with comma; semicolon as coordinate separator
  const lblW  = labelDecimalXY(pFrac, qFrac, "W");
  const lblX1 = labelDecimalXY(x1Frac, { n: 0, d: 1 });
  const lblX2 = labelDecimalXY(x2Frac, { n: 0, d: 1 });
  const lblY0 = labelDecimalXY({ n: 0, d: 1 }, data.y0Frac);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Dane i wzory:</div>
      <BlockMath math={`f(x) = ${formatQuadraticLatexWithMixedC(a,b,data.y0Frac)}`} />
      <BlockMath math={'\\Delta = b^2 - 4ac,\\qquad x_{1,2} = \\dfrac{-b \\pm \\sqrt{\\Delta}}{2a},\\qquad p = -\\dfrac{b}{2a},\\ q = -\\dfrac{\\Delta}{4a},\\ y_0 = f(0) = c'} />

      <div><strong>Krok 2.</strong> Obliczenia (zwięźle):</div>
      <BlockMath math={`\\Delta = ${par(b)}^{2} - 4\\cdot${par(a)}\\cdot${par(c)} = ${b2} - ${fourAC < 0 ? `(${fourAC})` : `${fourAC}`} = ${delta}`} />
      <BlockMath math={`x_1 = \\dfrac{-${par(b)} - \\sqrt{${delta}}}{2\\cdot${par(a)}} = \\dfrac{${-b} - ${sqrtDelta}}{${twoA}} = ${formatFractionMixed(x1Frac.n, x1Frac.d)}`} />
      <BlockMath math={`x_2 = \\dfrac{-${par(b)} + \\sqrt{${delta}}}{2\\cdot${par(a)}} = \\dfrac{${-b} + ${sqrtDelta}}{${twoA}} = ${formatFractionMixed(x2Frac.n, x2Frac.d)}`} />
      <BlockMath math={`p = -\\dfrac{${par(b)}}{2\\cdot${par(a)}} = -\\dfrac{${b}}{${twoA}} = ${formatFractionMixed(pFrac.n, pFrac.d)}`} />
      <BlockMath math={`q = -\\dfrac{${delta}}{4\\cdot${par(a)}} = -\\dfrac{${delta}}{${4*a}} = ${formatFractionMixed(data.qFrac.n, data.qFrac.d)}`} />
      <BlockMath math={`y_0 = c = ${formatFractionMixed(data.y0Frac.n, data.y0Frac.d)}`} />

      <div><strong>Wynik:</strong></div>
      <BlockMath math={`x_1 = ${formatFractionMixed(x1Frac.n,x1Frac.d)},\\quad x_2 = ${formatFractionMixed(x2Frac.n,x2Frac.d)}`} />
      {/* NOWE: wierzchołek jako W = (...) */}
      <BlockMath math={`W = \\Big(${formatFractionMixed(pFrac.n,pFrac.d)},\\ ${formatFractionMixed(data.qFrac.n,data.qFrac.d)}\\Big),\\qquad y_0 = ${formatFractionMixed(data.y0Frac.n, data.y0Frac.d)}`} />

      <div style={{ marginTop: "1rem" }}><strong>Wizualizacja:</strong></div>
      <GraphViewer
        points={{
          X1: { x: x1, y: 0, label: lblX1, color: "#FFA500" },
          X2: { x: x2, y: 0, label: lblX2, color: "#FFA500" },
          W:  { x: p,  y: q, label: lblW,  color: "#FFA500" },
          Y0: { x: 0,  y: data.y0, label: lblY0, color: "#FFA500" },
        }}
        equations={[
          { expr: `y = ${a} * x * x ${b >= 0 ? "+ " + b : "- " + Math.abs(b)} * x ${c >= 0 ? "+ " + c : "- " + Math.abs(c)}`, color: "#2e7d32", label: "parabola" }
        ]}
        xRange={[-8, 8]}
        yRange={[-8, 8]}
        showGrid
        width={520}
        height={520}
      />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderUserAnswer,
  renderAnswer,
  renderExplanation,
};
